import React, { useEffect, useContext, useRef } from "react";
import {
  useLocation,
  useHistory,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import ChangePassword from "./ChangePassword/ChangePassword";
import ChooseAccount from "./ChooseAccount/ChooseAccount";
import LoginPage from "./Login/LoginPage";
import "./Login/LoginPage.scss";
import SignInMFA from "./MFA/SignInMFA";
import { dismissAllToasts } from "../Forms/Dialogs/Toast/Toast";
import {
  AuthContext,
  DEFAULT_AUTHENTICATED_TARGET,
  LOGGED_OUT_STATE,
} from "./AuthContext";
import { GeneralAppContext } from "../General/GeneralAppProvider";
import { useAppStyling } from "../WhiteLabel/useAppStyling";
import { useIcon } from "@SeemplicityDev/shared";
import {
  buildSamlPath,
  clearPostLoginRedirect,
  canAutoRedirectToSso,
  hasManualLogoutMarker,
  consumePostLoginRedirect,
  getLastSsoProvider,
  hasRecentSsoFailure,
  isSamlPath,
  isSsoAutoRedirectDisabled,
  isSsoDisabledByQuery,
  isSsoEnforced,
  markAutoRedirectToSso,
  markPendingSamlResumeAttempt,
  peekPendingSamlProvider,
} from "./ssoSession";

const LOGIN_PATH = "/login";
const ROOT_PATH = "/";
/** Substring predicate preserved from the original cleanup effect. */
const LOGOUT_PATH_FRAGMENT = "logout";

const AuthPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { appContext } = useContext(GeneralAppContext);
  const { layoutClass } = appContext;
  const { getSvg } = useIcon();

  const {
    updateAuthState,
    stopImpersonation,
    clearOrgAndAccount,
    authState: { authenticated, user },
  } = useContext(AuthContext);

  const { appProps } = useAppStyling();

  // The redirect decision is made once per mount: updateAuthState below
  // re-renders, and the pending entry's attempt counter must not be bumped
  // twice. It also keeps behaviour stable if React ever double-invokes effects.
  const hasDecidedRef = useRef(false);

  useEffect(() => {
    const pathname = location?.pathname ?? "";
    const search = location?.search ?? "";
    const isLogoutPath = pathname.includes(LOGOUT_PATH_FRAGMENT);
    const isAuthEntryPath =
      isLogoutPath || pathname === LOGIN_PATH || pathname === ROOT_PATH;

    // 1. /logout cleanup FIRST — same effect, before any navigation, so we
    //    never hand off to the IdP with a stale org / impersonation context.
    if (isLogoutPath) {
      stopImpersonation();
      clearOrgAndAccount();
      dismissAllToasts();
      updateAuthState(LOGGED_OUT_STATE);
      // The deep link belonged to the session that just ended. Deliberately
      // NOT cleared here: the IdP-failure and auto-redirect timestamps, which
      // are the loop guards — resetting them on /logout would let a persistent
      // backend 401 bounce app -> IdP -> app forever — and the pending /saml
      // entry, which is what the resume path is for.
      clearPostLoginRedirect();
    }

    // 2. Only the three unauthenticated entry paths are eligible. /mfa,
    //    /changePassword and the ChooseAccount step (user present, not yet
    //    authenticated) are deliberately untouched — hence the `!!user` guard
    //    rather than a check on `authenticated`.
    if (!isAuthEntryPath || !!user || hasDecidedRef.current) {
      return;
    }
    hasDecidedRef.current = true;

    // Read WITHOUT clearing: the veto must outlive this render, this tab and
    // this reload. Only a successful login or an explicit "Continue with SSO"
    // click lifts it (CWE-613 — a session-scoped, read-once veto let a new tab
    // silently re-login through a still-live IdP session).
    const isManualLogout = hasManualLogoutMarker();
    const pending = peekPendingSamlProvider();

    // 3. A captured /saml/<slug> entry wins outright: the user explicitly
    //    asked for the IdP and Amplify's hosted-UI logout stole the URL from
    //    them. Only the attempt counter is bumped here — AuthSAML consumes the
    //    entry, so a bootstrap that redirects away again can still resume.
    if (pending) {
      const pendingPath = buildSamlPath(pending.provider, search);
      if (pendingPath) {
        markPendingSamlResumeAttempt();
        history.replace(pendingPath);
        return;
      }
    }

    // 4. Ordered vetoes: previews/localhost (pasted prod sessions; Cognito
    //    never redirects back there, so the failure veto could not fire — see
    //    isSsoAutoRedirectDisabled), deliberate logout, then a recent IdP
    //    failure, then the ?sso=off escape hatch, then the enforced-org +
    //    known-provider requirement, then the ~30 s rate guard.
    if (isSsoAutoRedirectDisabled()) {
      return;
    }
    if (isManualLogout) {
      return;
    }
    if (hasRecentSsoFailure()) {
      return;
    }
    if (isSsoDisabledByQuery(search)) {
      return;
    }

    const provider = getLastSsoProvider();
    if (!isSsoEnforced() || !provider) {
      return;
    }
    if (!canAutoRedirectToSso()) {
      return;
    }

    const samlPath = buildSamlPath(provider, search);
    if (!samlPath) {
      return;
    }
    markAutoRedirectToSso();
    history.replace(samlPath);
  }, [
    location?.pathname,
    location?.search,
    user,
    history,
    stopImpersonation,
    clearOrgAndAccount,
    updateAuthState,
  ]);

  if (authenticated) {
    const redirectTo =
      consumePostLoginRedirect() ?? DEFAULT_AUTHENTICATED_TARGET;
    return <Redirect to={redirectTo} />;
  }

  // Never paint the password form while handing off to the IdP: once the
  // effect above has replaced the URL with /saml/<provider>, this render is
  // just the tail of that navigation.
  if (isSamlPath(location?.pathname)) {
    return null;
  }

  return (
    <div className={`login-page-wrap ${layoutClass}`}>
      <div
        className="login-page-background-image"
        style={{ backgroundImage: `url("${getSvg("login-bg")}")` }}
      />
      <div className="login-page-header">
        <img className={"login-logo-container"} src={appProps?.logo?.login} />
      </div>
      <Switch>
        <Route path="/changePassword" component={ChangePassword} />
        <Route path="/mfa" component={SignInMFA} />
        <Route component={!user ? LoginPage : ChooseAccount} />
      </Switch>
    </div>
  );
};

export default AuthPage;
